import * as React from "react";
import { Step, CallBackProps, EVENTS, ACTIONS, STATUS } from "react-joyride";
import Joyride from "react-joyride";
import { AppState, getTourSteps, tourStartedAction } from "@store";
import { connect, Dispatch } from "react-redux";
import { Tours } from "@common";

import "./Tour.css";
import { SvgIcon } from "../SvgIcon";
import { CloseIcon } from "@icons";
import { scrollIntoView } from "./scrollIntoView";
import { ModalWindow } from "@components";

const Tooltip = ({ continuous, index, step, backProps, closeProps, primaryProps, tooltipProps, skipProps }: any) => (
    <div className="tour__tooltip-wrapper" {...tooltipProps}>
        <fieldset className="tour__tooltip-fieldset">
            <legend className="tour__tooltip-legend">Guide</legend>
            {step.title && <div>{step.title}</div>}
            <div className="tour__tooltip-content">
                <div>{step.content}</div>
                <button className="tour__btn tour__btn_close" {...skipProps}>
                    <SvgIcon className="tour__svg-close" svg={CloseIcon} />
                </button>
            </div>
            <div className="tour__btn-wrapper">
                {index > 0 && (
                    <button className="tour__btn tour__btn_back" {...backProps}>
                        <span id="back">Back</span>
                    </button>
                )}
                {continuous && (
                    <button className="tour__btn" {...primaryProps}>
                        <span id="next" />
                    </button>
                )}
                {!continuous && (
                    <button className="tour__btn tour__btn_next" {...closeProps}>
                        <span id="close">Next</span>
                    </button>
                )}
            </div>
        </fieldset>
    </div>
);

function GuideViewConfirmation(props: { isOpen: boolean; onConfirm: () => void; onCancel: () => void }) {
    return (
        <ModalWindow state={props.isOpen} modalId="guideModal">
            <div className="guide-modal">
                <div className="guide-modal__content">Would you like to have a tour through the user interface?</div>
                <div className="guide-modal__buttons">
                    <button className="guide-modal__btn" onClick={props.onConfirm}>
                        Yes
                    </button>
                    <button className="guide-modal__btn" onClick={props.onCancel}>
                        No
                    </button>
                </div>
            </div>
        </ModalWindow>
    );
}

interface TourComponentProps {
    steps: Step[];
    isRunning: boolean;
    onTourShown: () => void;
}
interface TourComponentState {
    isRunning: boolean;
    stepIndex: number;
    isConfirmationShown: boolean;
}

class TourComponent extends React.Component<TourComponentProps, TourComponentState> {
    state = {
        isRunning: true,
        stepIndex: 0,
        isConfirmationShown: true
    };

    floaterProps = {
        disableAnimation: true
    };

    render() {
        const { steps, isRunning } = this.props;

        return (
            <>
                <GuideViewConfirmation
                    isOpen={this.state.isConfirmationShown && isRunning}
                    onCancel={this.cancelGuide}
                    onConfirm={this.confirmGuide}
                />
                <Joyride
                    steps={steps || []}
                    stepIndex={this.state.stepIndex}
                    run={isRunning && this.state.isRunning && !this.state.isConfirmationShown}
                    showProgress
                    callback={this.joyrideCallback}
                    disableScrollParentFix
                    disableScrolling
                    floaterProps={this.floaterProps as any}
                    tooltipComponent={Tooltip}
                    spotlightPadding={2}
                />
            </>
        );
    }

    joyrideCallback = (data: CallBackProps) => {
        if (data.type === EVENTS.TOUR_END) {
            this.props.onTourShown && this.props.onTourShown();
        }

        if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].indexOf(data.type as any) !== -1) {
            // Update state to advance the tour
            const nextStepIndex = data.index + (data.action === ACTIONS.PREV ? -1 : 1);
            const nextStep = this.props.steps[nextStepIndex];
            if (nextStep) {
                const selector = nextStep.target;
                if (typeof selector === "string") {
                    const element = document.querySelector(selector);
                    if (element) {
                        scrollIntoView(element as HTMLElement).then(() => this.setState({ stepIndex: nextStepIndex }));
                    } else {
                        this.setState({ stepIndex: nextStepIndex });
                    }
                }
            } else {
                this.setState({ stepIndex: nextStepIndex });
            }
        } else if ([STATUS.FINISHED, STATUS.SKIPPED].indexOf(data.status as any) !== -1) {
            // Need to set our running state to false, so we can restart if we click start again.
            this.setState({ isRunning: false });
            this.props.onTourShown && this.props.onTourShown();
        }
    };

    cancelGuide = () => {
        this.setState({ isConfirmationShown: false, isRunning: false });
        this.props.onTourShown && this.props.onTourShown();
    };

    confirmGuide = () => this.setState({ isConfirmationShown: false });
}

export interface TourProps {
    tour: Tours;
    isRunning: boolean;
}

export const Tour = connect(
    (appState: AppState, ownProps: TourProps): Partial<TourComponentProps> => {
        const info = getTourSteps(appState, ownProps.tour);

        return {
            steps: info ? info.steps : [],
            isRunning: ownProps.isRunning && info && info.show
        };
    },
    (dispatch: Dispatch, ownProps: TourProps): Partial<TourComponentProps> => ({
        onTourShown() {
            dispatch(tourStartedAction({ tour: ownProps.tour }));
        }
    })
)(TourComponent);
